import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ErrorApi } from '../../../api/problema';
import type { CampoPropio, Etiqueta, FiltrosClientes, ListaClientes, OrdenClientes, ResumenCliente } from '../../../api/ops/clientes';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Fecha } from '../../../componentes/Fecha';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { Tabla, type FilaTabla } from '../../../componentes/Tabla';
import { rolesDe } from '../../../logica/acceso';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { estadoDeError } from '../../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { useAnchoVentana } from '../../../marco/tamano';
import { Avatar } from '../../../marco/piezas';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { Alta } from '../alta/Alta';
import { cuerpoAlta, formularioVacio, type DatosFormulario, type ErroresFormulario } from '../alta/datos';
import { Ficha } from '../ficha/Ficha';
import type { ContextoClientes } from './contexto';
import { AYUDA, BLOQUEADA, PERMISOS, consultaDeBusqueda, diaCorto, erroresDeCampos, inicialesDe, telefonoLegible, textoDeError, type PestanaFicha } from './textos';
import './clientes.css';

/**
 * Lista de clientes (2.1): tabla en ordenador y tablet (por debajo de 1280 px el teléfono pasa bajo el nombre) y
 * tarjetas en móvil, con «Anterior / Siguiente» sin total (G121). Encima, el filtro por etiqueta sin cifras (G140), la
 * búsqueda por nombre, teléfono o email completos y «Ver los clientes» (cuenta, orden y un filtro por cada campo propio
 * de una opción, sí o no y fecha, G142). Etiquetas como chips neutros (G143). Con el ámbito «lo suyo» de
 * `clientes.fichas.ver`, la línea «Con tu rol ves solo…» tal cual llega en `scope_note` (G363; la API ya filtra). «Nuevo cliente» abre el alta (2.2) y
 * `?cliente=` la ficha (2.3).
 */
const ORDENES: readonly { valor: OrdenClientes; texto: string }[] = [
    { valor: '-last_visit_at', texto: 'Última visita' },
    { valor: 'name', texto: 'Nombre' },
    { valor: '-created_at', texto: 'Alta más reciente' },
    { valor: 'created_at', texto: 'Alta más antigua' },
];

/** Por debajo de este ancho, el teléfono va bajo el nombre (para que «Última visita» y «Cuenta» entren a 1024). */
const ANCHO_TELEFONO = 1280;

interface Filtros {
    texto: string;
    etiqueta: string | null;
    cuenta: '' | 'local' | 'linked';
    orden: OrdenClientes;
    campos: Record<string, string>;
}

const SIN_FILTROS: Filtros = { texto: '', etiqueta: null, cuenta: '', orden: '-last_visit_at', campos: {} };

function hayFiltros(f: Filtros): boolean {
    return f.texto.trim() !== '' || f.etiqueta !== null || f.cuenta !== '' || Object.values(f.campos).some((v) => v !== '');
}

/** Campos propios por los que se puede filtrar (G142): de una opción, sí o no y fecha. */
function filtrables(campos: readonly CampoPropio[]): CampoPropio[] {
    return campos.filter((c) => c.archived_at == null && (c.type === 'select' || c.type === 'boolean' || c.type === 'date'));
}

export function Lista(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, busqueda, ruta, navegar, membresia, nombreNegocio } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const pag = usePaginacionCursor();
    const ancho = useAnchoVentana();
    const [filtros, setFiltros] = useState<Filtros>(SIN_FILTROS);
    const [buscado, setBuscado] = useState('');
    const [verFiltros, setVerFiltros] = useState(false);
    const [lista, setLista] = useState<ListaClientes | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
    const [campos, setCampos] = useState<CampoPropio[]>([]);
    const [alta, setAlta] = useState<{ clave: number; inicial: { datos: DatosFormulario; errores: ErroresFormulario } | null } | null>(null);
    const [provisional, setProvisional] = useState<ResumenCliente | null>(null);
    const claveAlta = useRef(0);

    const puedeCrear = permisos.puede(PERMISOS.crear) && !soloLectura;
    const clienteAbierto = busqueda.get('cliente');
    const pestana = busqueda.get('pestana') as PestanaFicha | null;

    // La búsqueda sale al dejar de escribir.
    useEffect(() => {
        const t = setTimeout(() => {
            setBuscado(filtros.texto);
        }, 300);
        return () => {
            clearTimeout(t);
        };
    }, [filtros.texto]);

    // Etiquetas y campos propios (para los filtros, la lista, el alta y la ficha).
    useEffect(() => {
        let vivo = true;
        ops.listTags(negocio, { limit: 100 })
            .then((l) => {
                if (vivo) {
                    setEtiquetas(l.data.filter((t) => t.archived_at == null));
                }
            })
            .catch(() => undefined);
        ops.listCustomFields(negocio)
            .then((l) => {
                if (vivo) {
                    setCampos(l.data);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    const consulta = useMemo<FiltrosClientes>(
        () => ({
            ...consultaDeBusqueda(buscado),
            tag_id: filtros.etiqueta ?? undefined,
            status: filtros.cuenta === '' ? undefined : filtros.cuenta,
            sort: filtros.orden,
            campos: filtros.campos,
        }),
        [buscado, filtros.etiqueta, filtros.cuenta, filtros.orden, filtros.campos],
    );
    const firma = JSON.stringify(consulta);
    const { reiniciar } = pag;
    useEffect(() => {
        reiniciar();
    }, [firma, reiniciar]);

    useEffect(() => {
        let vivo = true;
        ops.listCustomers(negocio, { ...(JSON.parse(firma) as FiltrosClientes), cursor: pag.cursor })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, firma, pag.cursor, intento]);

    const refrescarLista = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const abrirFicha = useCallback(
        (id: string, p?: PestanaFicha) => {
            const q = new URLSearchParams({ cliente: id });
            if (p !== undefined && p !== 'resumen') {
                q.set('pestana', p);
            }
            navegar(`${ruta}?${q.toString()}`);
        },
        [navegar, ruta],
    );
    const cerrarFicha = useCallback(() => {
        navegar(ruta);
    }, [navegar, ruta]);

    const abrirAlta = (previo: Partial<DatosFormulario> = {}) => {
        claveAlta.current += 1;
        setAlta({ clave: claveAlta.current, inicial: Object.keys(previo).length === 0 ? null : { datos: formularioVacio(previo), errores: {} } });
    };

    /** Si al enviarse la API rechaza un dato (409 de otro perfil, 422), vuelve a abrir el alta con el error en su campo. */
    const fallaAlta = (d: DatosFormulario, e: unknown) => {
        const campos = erroresDeCampos(e);
        if (Object.keys(campos).length > 0) {
            claveAlta.current += 1;
            setAlta({ clave: claveAlta.current, inicial: { datos: d, errores: campos } });
            return;
        }
        avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido dar de alta al cliente. Prueba de nuevo.') });
    };

    const enviarAlta = (d: DatosFormulario) =>
        ops.createCustomer(negocio, cuerpoAlta(d, campos)).then((r) => {
            if (r.outcome === 'matched_existing') {
                avisar({ tipo: 'ok', texto: 'Ya existía; abrimos su ficha.' });
                abrirFicha(r.customer.id);
            }
            refrescarLista();
            return r;
        });

    // G159: sin información comercial, el alta sale a los 5 s salvo «Deshacer».
    const darDeAlta = (d: DatosFormulario) => {
        setAlta(null);
        const nombre = d.nombre.trim() !== '' ? d.nombre.trim() : d.telefono.trim() !== '' ? d.telefono.trim() : d.email.trim();
        setProvisional({ id: 'cus_provisional', status: 'local', display_name: nombre, phone: d.telefono.trim() === '' ? null : d.telefono.trim(), email: d.email.trim() === '' ? null : d.email.trim(), tag_ids: d.etiquetas, last_visit_at: null, visits_count: 0 });
        envio.programar({
            texto: `${nombre} ya está en tus clientes.`,
            donde: 'lista',
            enviar: () => {
                setProvisional(null);
                enviarAlta(d).catch((e: unknown) => {
                    fallaAlta(d, e);
                });
            },
            alDeshacer: () => {
                setProvisional(null);
            },
        });
    };

    // Con información comercial avisa al cliente: sin «Deshacer», se envía ya y se abre su ficha en Permisos.
    const darDeAltaYAvisar = (d: DatosFormulario) =>
        enviarAlta(d)
            .then((r) => {
                setAlta(null);
                if (r.outcome === 'created') {
                    avisar({ tipo: 'ok', texto: `${r.customer.display_name} ya está en tus clientes. Le hemos enviado un mensaje para que confirme la información comercial.` });
                    abrirFicha(r.customer.id, 'permisos');
                }
            })
            .catch((e: unknown) => {
                setAlta(null);
                fallaAlta(d, e);
                throw e;
            });

    const acciones: AccionCabecera[] = puedeCrear ? [{ clave: 'nuevo', texto: 'Nuevo cliente', icono: 'user-plus', onClick: () => { abrirAlta(); } }] : [];
    useCabecera({ ayuda: AYUDA.lista, acciones });

    const c: ContextoClientes = {
        ops,
        negocio,
        movil,
        soloLectura,
        permisos,
        avisar,
        etiquetas,
        campos,
        programar: envio.programar,
        pendiente: envio.pendiente,
        deshacer: envio.deshacer,
        duracion: envio.duracion,
        abrirFicha,
        cerrarFicha,
        refrescarLista,
    };

    const nombreEtiqueta = (id: string) => etiquetas.find((t) => t.id === id)?.name ?? null;
    const conFiltros = hayFiltros(filtros);
    const camposFiltro = filtrables(campos);
    const busquedaActual = consultaDeBusqueda(buscado);

    const quitarFiltros = () => {
        setFiltros(SIN_FILTROS);
        setBuscado('');
    };

    const barraFiltros = (
        <div className="rv-cl-filtros">
            <div className="rv-cl-etiquetas" role="radiogroup" aria-label="Filtrar por etiqueta">
                <button
                    type="button"
                    role="radio"
                    aria-checked={filtros.etiqueta === null}
                    className="rv-cl-pildora"
                    onClick={() => {
                        setFiltros((f) => ({ ...f, etiqueta: null }));
                    }}
                >
                    Todas
                </button>
                {etiquetas.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="radio"
                        aria-checked={filtros.etiqueta === t.id}
                        className="rv-cl-pildora"
                        onClick={() => {
                            setFiltros((f) => ({ ...f, etiqueta: f.etiqueta === t.id ? null : t.id }));
                        }}
                    >
                        {t.name}
                    </button>
                ))}
            </div>
            <div className="rv-cl-buscar">
                <label className="rv-cl-buscar__caja">
                    <Icono nombre="search" />
                    <input
                        type="search"
                        aria-label="Buscar por nombre, teléfono o email"
                        placeholder="Nombre, teléfono o email"
                        value={filtros.texto}
                        autoComplete="off"
                        onChange={(e) => {
                            setFiltros((f) => ({ ...f, texto: e.target.value }));
                        }}
                    />
                    {filtros.texto !== '' && (
                        <button
                            type="button"
                            className="rv-cl-buscar__quitar"
                            aria-label="Borrar la búsqueda"
                            onClick={() => {
                                setFiltros((f) => ({ ...f, texto: '' }));
                            }}
                        >
                            <Icono nombre="x" />
                        </button>
                    )}
                </label>
                <button
                    type="button"
                    className={`rv-bi rv-cl-ajustes${verFiltros ? ' rv-cl-ajustes--abierto' : ''}`}
                    aria-label="Ver los clientes"
                    aria-expanded={verFiltros}
                    onClick={() => {
                        setVerFiltros((v) => !v);
                    }}
                >
                    <Icono nombre="sliders-horizontal" />
                </button>
            </div>
            {verFiltros && (
                <div className="rv-cl-vista" role="dialog" aria-label="Ver los clientes">
                    <span className="rv-cl-ceja rv-cl-ceja--gris">Ver los clientes</span>
                    <div className="rv-cl-vista__rejilla">
                        {camposFiltro.map((campo) =>
                            campo.type === 'date' ? (
                                <Fecha
                                    key={campo.id}
                                    etiqueta={campo.label}
                                    valor={filtros.campos[campo.key] ?? ''}
                                    onChange={(v) => {
                                        setFiltros((f) => ({ ...f, campos: { ...f.campos, [campo.key]: v } }));
                                    }}
                                />
                            ) : (
                                <Desplegable
                                    key={campo.id}
                                    etiqueta={campo.label}
                                    valor={filtros.campos[campo.key] ?? ''}
                                    opciones={[
                                        { valor: '', texto: 'Cualquiera' },
                                        ...(campo.type === 'boolean'
                                            ? [
                                                  { valor: 'true', texto: 'Sí' },
                                                  { valor: 'false', texto: 'No' },
                                              ]
                                            : (campo.options ?? []).map((o) => ({ valor: o, texto: o }))),
                                    ]}
                                    onChange={(v) => {
                                        setFiltros((f) => ({ ...f, campos: { ...f.campos, [campo.key]: v } }));
                                    }}
                                />
                            ),
                        )}
                        <Desplegable
                            etiqueta="Cuenta Reservaria"
                            valor={filtros.cuenta}
                            opciones={[
                                { valor: '', texto: 'Todas' },
                                { valor: 'linked', texto: 'Vinculada' },
                                { valor: 'local', texto: 'Sin vincular' },
                            ]}
                            onChange={(v) => {
                                setFiltros((f) => ({ ...f, cuenta: v as Filtros['cuenta'] }));
                            }}
                        />
                        <Desplegable
                            etiqueta="Ordenar por"
                            valor={filtros.orden}
                            opciones={ORDENES}
                            onChange={(v) => {
                                setFiltros((f) => ({ ...f, orden: v as OrdenClientes }));
                            }}
                        />
                    </div>
                    <div className="rv-cl-vista__pie">
                        <Boton
                            tipo="secundario"
                            icono="check"
                            className="rv-cl-listo"
                            movil={movil}
                            onClick={() => {
                                setVerFiltros(false);
                            }}
                        >
                            Listo
                        </Boton>
                    </div>
                </div>
            )}
        </div>
    );

    // La frase la compone el servidor según los roles con ámbito «lo suyo» (`scope_note`, G363); el panel no la escribe.
    const notaAmbito = lista?.scope_note ?? null;
    const lineaRol = notaAmbito !== null && notaAmbito !== '' && (
        <p className="rv-cl-rol">
            <Icono nombre="eye" />
            {notaAmbito}
        </p>
    );

    const aviso = envio.pendiente !== null && (clienteAbierto === null || envio.pendiente.donde === 'lista') && (
        <Deshacer key={envio.pendiente.clave} clave={envio.pendiente.clave} texto={envio.pendiente.texto} duracion={envio.duracion} movil={movil} onDeshacer={envio.deshacer} />
    );

    const ficha = clienteAbierto !== null && (
        <Ficha key={clienteAbierto} c={c} id={clienteAbierto} pestanaInicial={pestana !== null && ['resumen', 'datos', 'historial', 'permisos', 'notas', 'entregas'].includes(pestana) ? pestana : 'resumen'} nombreNegocio={nombreNegocio} />
    );

    const panelAlta = alta !== null && (
        <Alta
            key={alta.clave}
            abierto
            movil={movil}
            campos={campos}
            etiquetas={etiquetas}
            inicial={alta.inicial}
            onCerrar={() => {
                setAlta(null);
            }}
            onDarDeAlta={darDeAlta}
            onDarDeAltaYAvisar={darDeAltaYAvisar}
        />
    );

    let contenido;
    if (error !== null && lista === null) {
        contenido =
            error instanceof ErrorApi && estadoDeError(error).tipo === 'sin-permiso' ? (
                <EstadoSinPermiso seccion="Clientes" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={null} movil={movil} />
            ) : (
                <EstadoError seccion="Clientes" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={refrescarLista} movil={movil} />
            );
    } else if (lista === null) {
        contenido = <EstadoCargando seccion="Clientes" />;
    } else {
        const filas = [...(provisional !== null && pag.cursor === null ? [provisional] : []), ...lista.data];
        if (filas.length === 0) {
            contenido = conFiltros ? (
                <Vacia
                    icono={busquedaActual.phone !== undefined || busquedaActual.email !== undefined ? 'search' : 'inbox'}
                    titulo={
                        busquedaActual.phone !== undefined
                            ? `No hay ningún cliente de tu negocio con el teléfono ${filtros.texto.trim()}`
                            : busquedaActual.email !== undefined
                              ? `No hay ningún cliente de tu negocio con el email ${filtros.texto.trim()}`
                              : 'No hay clientes con estos filtros.'
                    }
                    texto={busquedaActual.phone !== undefined || busquedaActual.email !== undefined ? 'Se busca solo entre tus clientes, con el teléfono o el email completos.' : 'Prueba con otra etiqueta o quita la búsqueda.'}
                >
                    <Boton tipo="secundario" icono="rotate-ccw" movil={movil} onClick={quitarFiltros}>
                        Quitar filtros
                    </Boton>
                    {puedeCrear && (busquedaActual.phone !== undefined || busquedaActual.email !== undefined) && (
                        <Boton
                            icono="user-plus"
                            movil={movil}
                            onClick={() => {
                                abrirAlta(busquedaActual.phone !== undefined ? { telefono: filtros.texto.trim() } : { email: filtros.texto.trim() });
                            }}
                        >
                            {busquedaActual.phone !== undefined ? 'Dar de alta con este teléfono' : 'Dar de alta con este email'}
                        </Boton>
                    )}
                </Vacia>
            ) : (
                <Vacia
                    icono="inbox"
                    ceja="Vacía"
                    titulo="Todavía no hay clientes"
                    texto={`Se crean solos con cada reserva, cita o entrada. También puedes darlos de alta tú${permisos.puede(PERMISOS.importar) ? ' o importarlos desde un CSV.' : '.'}`}
                />
            );
        } else {
            const conTelefono = !movil && ancho >= ANCHO_TELEFONO;
            const filasTabla: FilaTabla[] = filas.map((f) => {
                const provisionalFila = f.id === 'cus_provisional';
                const nombre = f.display_name === '' ? 'Sin nombre' : f.display_name;
                const tel = telefonoLegible(f.phone);
                const linea = conTelefono ? (f.email ?? 'Sin email') : tel !== '' ? tel : (f.email ?? 'Sin contacto');
                const chips = (f.tag_ids ?? []).map(nombreEtiqueta).filter((n): n is string => n !== null);
                const etiquetasCelda = chips.length === 0 ? '—' : (
                    <span className="rv-cl-chips">
                        {chips.slice(0, 2).map((n) => (
                            <Chip key={n} estado="neutral">
                                {n}
                            </Chip>
                        ))}
                        {chips.length > 2 && <Chip estado="neutral">{`+${String(chips.length - 2)}`}</Chip>}
                    </span>
                );
                const ultima = f.last_visit_at == null ? 'Sin visitas' : diaCorto(f.last_visit_at);
                const cuenta =
                    f.status === 'blocked' ? (
                        <Chip estado="problem">{BLOQUEADA}</Chip>
                    ) : f.status === 'linked' ? (
                        <span className="rv-cl-cuenta">
                            <Icono nombre="circle-check" />
                            Vinculada
                        </span>
                    ) : (
                        <span className="rv-cl-cuenta rv-cl-cuenta--no">
                            <Icono nombre="unlink" />
                            Sin vincular
                        </span>
                    );
                const persona = (
                    <span className="rv-cl-persona">
                        <Avatar texto={inicialesDe(nombre)} tamano={36} fondo="var(--rv-ink)" tinta="#fff" />
                        <span>
                            <strong>
                                {nombre}
                                {movil && f.status === 'linked' && <Icono nombre="circle-check" />}
                            </strong>
                            {!movil && <span>{linea}</span>}
                        </span>
                    </span>
                );
                return {
                    clave: f.id,
                    etiqueta: `Abrir la ficha de ${nombre}`,
                    onAbrir: provisionalFila
                        ? undefined
                        : () => {
                              abrirFicha(f.id);
                          },
                    celdas: { persona, telefono: tel === '' ? '—' : tel, etiquetas: etiquetasCelda, visitas: <strong>{f.visits_count ?? 0}</strong>, ultima, cuenta },
                    tarjeta: (
                        <span className="rv-cl-tarjeta">
                            {persona}
                            <span className="rv-cl-tarjeta__linea">{`${tel !== '' ? tel : (f.email ?? 'Sin contacto')} • ${f.last_visit_at == null ? 'Sin visitas' : `Última visita ${ultima.toLocaleLowerCase('es')}`}`}</span>
                            {(chips.length > 0 || f.status === 'blocked') && (
                                <span className="rv-cl-chips">
                                    {f.status === 'blocked' && <Chip estado="problem">{BLOQUEADA}</Chip>}
                                    {chips.map((n) => (
                                        <Chip key={n} estado="neutral">
                                            {n}
                                        </Chip>
                                    ))}
                                </span>
                            )}
                        </span>
                    ),
                };
            });
            contenido = (
                <>
                    <Tabla
                        etiqueta="Clientes"
                        movil={movil}
                        columnas={[
                            { clave: 'persona', titulo: 'Cliente', ancho: 'minmax(0, 2.2fr)' },
                            ...(conTelefono ? [{ clave: 'telefono', titulo: 'Teléfono', ancho: 'minmax(0, 1.3fr)' }] : []),
                            { clave: 'etiquetas', titulo: 'Etiquetas', ancho: 'minmax(0, 1.8fr)' },
                            { clave: 'visitas', titulo: 'Visitas', ancho: '64px', alinear: 'derecha' as const },
                            { clave: 'ultima', titulo: 'Última visita', ancho: 'minmax(96px, 0.9fr)' },
                            { clave: 'cuenta', titulo: 'Cuenta', ancho: 'minmax(120px, 1fr)' },
                        ]}
                        filas={filasTabla}
                    />
                    {(pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                        <div className="rv-cl-pie">
                            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
                        </div>
                    )}
                </>
            );
        }
    }

    const sinNada = lista !== null && lista.data.length === 0 && !conFiltros && provisional === null;
    return (
        <div className="rv-clientes">
            {!sinNada && error === null && barraFiltros}
            {lineaRol}
            {aviso}
            {contenido}
            {panelAlta}
            {ficha}
        </div>
    );
}

function Vacia({ icono, ceja, titulo, texto, children }: { icono: 'inbox' | 'search'; ceja?: string; titulo: string; texto: string; children?: ReactNode }) {
    return (
        <div className="rv-estado">
            <span className="rv-estado__baldosa" style={{ background: 'var(--rv-bone)', color: 'var(--rv-fg-2)' }}>
                <Icono nombre={icono} />
            </span>
            {ceja !== undefined && (
                <div className="rv-estado__ceja" style={{ color: 'var(--rv-fg-3)' }}>
                    {ceja}
                </div>
            )}
            <div className="rv-estado__titulo">{titulo}</div>
            <div className="rv-estado__texto">{texto}</div>
            {children !== undefined && <div className="rv-estado__botones">{children}</div>}
        </div>
    );
}
